/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, useEffect, useState } from "react";
import { type MatrixClient } from "matrix-js-sdk";
import { useTranslation } from "react-i18next";
import { Heading } from "@vector-im/compound-web";
import styles from "./CallEndedView.module.css";
import { useProfile } from "../profile/useProfile";
import { Header, HeaderLogo, LeftNav, RightNav } from "../Header";
import { backendFetch } from "../utils/backend";
import { isGuestOrigin } from "../auth/access";

interface Props {
  client: MatrixClient;
  isPasswordlessUser: boolean;
  hideHeader: boolean;
  confineToRoom: boolean;
  endedCallId: string;
}

export const CallEndedView: FC<Props> = ({
  client,
  isPasswordlessUser,
  hideHeader,
  confineToRoom,
}) => {
  const { t, i18n } = useTranslation();
  const { displayName } = useProfile(client);
  const [verifiedClient, setVerifiedClient] = useState<MatrixClient>();
  useEffect(() => {
    if (isPasswordlessUser || client.isGuest() || confineToRoom) return;
    const controller = new AbortController();
    void backendFetch(client, "/api/me", { signal: controller.signal })
      .then(async (response) => await response.json())
      .then((me) => {
        if (
          !controller.signal.aborted &&
          me.userId === client.getUserId() &&
          ["owner", "organizer"].includes(me.role)
        )
          setVerifiedClient(client);
      })
      .catch(() => {
        /* A failed role check must never expose staff navigation. */
      });
    return () => controller.abort();
  }, [client, isPasswordlessUser, confineToRoom]);

  let home: string | undefined;
  const admin = import.meta.env.VITE_ADMIN_URL;
  if (
    verifiedClient === client &&
    !isPasswordlessUser &&
    !client.isGuest() &&
    !confineToRoom &&
    (admin || !isGuestOrigin(window.location.origin))
  ) {
    const target = new URL("/", admin || window.location.origin);
    target.searchParams.set(
      "theme",
      document.body.classList.contains("cpd-theme-light") ||
        document.body.classList.contains("cpd-theme-light-hc")
        ? "light"
        : "dark",
    );
    target.searchParams.set(
      "lang",
      i18n.resolvedLanguage || i18n.language || "en",
    );
    home = target.href;
  }
  return (
    <>
      {!hideHeader && (
        <Header>
          <LeftNav>{!confineToRoom && <HeaderLogo />}</LeftNav>
          <RightNav />
        </Header>
      )}
      <div className={styles.container}>
        <main className={styles.main}>
          <Heading size="xl" weight="semibold" className={styles.headline}>
            {t("call_ended_view.headline", { displayName })}
          </Heading>

          {home && (
            <a
              href={home}
              className={`glass-button glass-accent ${styles.homeButton}`}
            >
              {t("call_ended_view.home_button")}
            </a>
          )}
        </main>
      </div>
    </>
  );
};
